import React, { useState, useEffect } from 'react';
import { 
  Bot,
  Pause, 
  Play, 
  AlertCircle, 
  RefreshCw, 
  Clock, 
  Zap,
  MessageSquare,
  CheckCircle2,
  HeartHandshake,
  ShoppingBag,
  Shield,
  Activity
} from 'lucide-react';
import { flowControlApi, FlowStatus } from '../services/flowControlApi.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

const FLOW_TYPES = [
  { 
    key: 'follow_up', 
    label: 'Follow-up', 
    description: 'Mensagens automatizadas para converter leads em agendamentos',
    icon: Zap,
    gradient: 'from-blue-500 to-blue-600',
    lightGradient: 'from-blue-50 to-blue-100',
    shadowColor: 'shadow-blue-500/30',
    iconBg: 'from-blue-400 to-blue-600'
  },
  { 
    key: 'noshow', 
    label: 'No-Show', 
    description: 'Mensagens para pacientes que faltaram à consulta',
    icon: MessageSquare,
    gradient: 'from-orange-500 to-orange-600',
    lightGradient: 'from-orange-50 to-orange-100',
    shadowColor: 'shadow-orange-500/30',
    iconBg: 'from-orange-400 to-orange-600'
  },
  { 
    key: 'confirmation', 
    label: 'Confirmação', 
    description: 'Confirmação automática de consultas agendadas',
    icon: CheckCircle2,
    gradient: 'from-green-500 to-green-600',
    lightGradient: 'from-green-50 to-green-100',
    shadowColor: 'shadow-green-500/30',
    iconBg: 'from-green-400 to-green-600'
  },
  { 
    key: 'pos_consulta', 
    label: 'Pós-Consulta', 
    description: 'Follow-up após o atendimento para fidelização',
    icon: HeartHandshake,
    gradient: 'from-purple-500 to-purple-600',
    lightGradient: 'from-purple-50 to-purple-100',
    shadowColor: 'shadow-purple-500/30',
    iconBg: 'from-purple-400 to-purple-600'
  },
  { 
    key: 'pos_venda', 
    label: 'Pós-Venda', 
    description: 'Acompanhamento após fechamento de tratamento',
    icon: ShoppingBag,
    gradient: 'from-pink-500 to-pink-600',
    lightGradient: 'from-pink-50 to-pink-100',
    shadowColor: 'shadow-pink-500/30',
    iconBg: 'from-pink-400 to-pink-600'
  }
];

export const FlowControlPanel: React.FC = () => {
  const { isDark } = useTheme();
  const [flowStates, setFlowStates] = useState<Record<string, FlowStatus>>({});
  const [loading, setLoading] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  
  const clinicId = Number(localStorage.getItem('clinic_id'));
  
  useEffect(() => {
    loadFlowStates();
    const interval = setInterval(loadFlowStates, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const loadFlowStates = async () => {
    try {
      setError(null);
      const states = await flowControlApi.getStatus(clinicId);
      setFlowStates(states);
    } catch (error: any) {
      console.error('Erro ao carregar estados:', error);
      setError(error.response?.data?.detail || 'Erro ao carregar status dos agentes');
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadFlowStates();
    setTimeout(() => setRefreshing(false), 500);
  };
  
  const handleToggle = async (flowType: string) => {
    const currentState = flowStates[flowType];
    const isPaused = currentState?.is_paused || false;
    
    if (!isPaused) {
      setSelectedFlow(flowType);
      setShowPauseModal(true);
      return;
    }
    
    confirmToggle(flowType, null);
  };
  
  const confirmToggle = async (flowType: string, reason: string | null) => {
    setLoading(flowType);
    setError(null);
    setShowPauseModal(false);
    setPauseReason('');
    
    try {
      const currentState = flowStates[flowType];
      const isPaused = currentState?.is_paused || false;
      
      await flowControlApi.toggleFlow(clinicId, {
        flow_type: flowType,
        is_paused: !isPaused,
        pause_reason: reason || undefined
      });
      
      await loadFlowStates();
    } catch (error: any) {
      console.error('Erro ao alternar estado:', error);
      setError(
        error.response?.data?.detail || 
        `Erro ao ${flowStates[flowType]?.is_paused ? 'retomar' : 'pausar'} agente`
      );
    } finally {
      setLoading(null);
    }
  };
  
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActiveFlowsCount = () => {
    return Object.values(flowStates).filter(state => !state.is_paused).length;
  };

  const getPausedFlowsCount = () => {
    return Object.values(flowStates).filter(state => state.is_paused).length;
  };
  
  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header Card estilo HubSpot */}
      <div className={`relative rounded-2xl border p-6 shadow-sm mb-6 max-w-7xl mx-auto ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-slate-100 text-slate-600'
          }`}>
            <Bot className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className={`text-xl md:text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Controle de Agentes
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-100">
                <Activity className="w-3 h-3 text-emerald-500" />
                {getActiveFlowsCount()} Ativos
              </span>
              {getPausedFlowsCount() > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-100">
                  <Pause className="w-3 h-3 text-red-500" />
                  {getPausedFlowsCount()} Pausados
                </span>
              )}
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              Gerencie a pausa e retomada dos agentes automáticos
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm hover:shadow transition ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' : 'border-slate-200 bg-white text-slate-700'}`}
            title="Atualizar status"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        {/* Erro Global */}
        {error && (
          <div className={`mt-4 p-4 rounded-xl border flex items-center gap-3 ${
            isDark 
              ? 'bg-red-900/30 border-red-700/50 text-red-300' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <AlertCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}
      </div>

      {/* Cards dos Fluxos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto mb-8">
        {FLOW_TYPES.map(flow => {
          const state = flowStates[flow.key];
          const isPaused = state?.is_paused || false;
          const Icon = flow.icon;
          
          return (
            <div 
              key={flow.key} 
              className={`
                relative rounded-2xl border shadow-sm p-5 transition hover:-translate-y-0.5 hover:shadow-md group
                ${isDark 
                  ? 'border-gray-700 bg-gray-800' 
                  : 'border-slate-200 bg-white'
                }
                ${loading === flow.key ? 'animate-pulse' : ''}
              `}
            >
              <div className="flex items-start gap-4">
                {/* Ícone padronizado */}
                <div className={`grid place-items-center rounded-xl w-10 h-10 ${
                  isPaused 
                    ? isDark 
                      ? 'bg-red-900/30 text-red-400' 
                      : 'bg-red-50 text-red-600'
                    : isDark 
                      ? 'bg-gray-700/50 text-gray-300' 
                      : 'bg-slate-100 text-slate-600'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>

                {/* Conteúdo */}
                <div className="flex-1">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{flow.label}</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{flow.description}</p>
                  
                  {/* Status info */}
                  {isPaused && state?.paused_at && (
                    <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg border ${
                      isDark 
                        ? 'border-red-700/50 bg-red-900/20 text-red-300' 
                        : 'border-red-200 bg-red-50 text-red-700'
                    }`}>
                      <Clock className="w-4 h-4" />
                      <div className="text-xs">
                        <p className="font-medium">
                          Pausado em {formatDateTime(state.paused_at)}
                        </p>
                        {state.pause_reason && (
                          <p className="mt-0.5 opacity-80">
                            Motivo: {state.pause_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {!isPaused && state?.resumed_at && (
                    <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg border ${
                      isDark 
                        ? 'border-green-700/50 bg-green-900/20 text-green-300' 
                        : 'border-green-200 bg-green-50 text-green-700'
                    }`}>
                      <Play className="w-4 h-4" />
                      <p className="text-xs font-medium">
                        Retomado em {formatDateTime(state.resumed_at)}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Botão de Ação */}
                <button
                  onClick={() => handleToggle(flow.key)}
                  disabled={loading === flow.key}
                  className={`
                    px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition
                    ${loading === flow.key ? 'opacity-50 cursor-not-allowed' : ''}
                    ${isPaused 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                    }
                  `}
                >
                  {loading === flow.key ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : isPaused ? (
                    <>
                      <Play className="w-4 h-4" />
                      Retomar
                    </>
                  ) : (
                    <>
                      <Pause className="w-4 h-4" />
                      Pausar
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Card de Informações */}
      <div className="max-w-7xl mx-auto">
        <div className={`rounded-2xl border shadow-sm p-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-slate-100 text-slate-600'
            }`}>
              <Shield className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Como Funciona o Controle de Agentes
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Pausar interrompe imediatamente o envio de novas mensagens</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Mensagens pausadas são verificadas diariamente às 8h</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>O timing original das mensagens é preservado</span>
                  </p>
                </div>
                <div className="space-y-3">
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Ao retomar, o agente continua de onde parou</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Todas as ações são registradas para auditoria</span>
                  </p>
                  <p className={`flex items-start gap-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <span className="w-2 h-2 bg-sminy-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Sistema monitora a saúde dos agentes 24/7</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Pausa */}
      {showPauseModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => {
              setShowPauseModal(false);
              setPauseReason('');
            }}
          />
          <div className={`relative z-[10000] w-full max-w-md rounded-2xl p-6 shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-slate-200'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Pausar Agente</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Deseja adicionar um motivo para a pausa? (opcional)
            </p>
            
            <textarea
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              placeholder="Ex: Manutenção programada, ajuste de mensagens..."
              className={`w-full px-4 py-3 rounded-xl border focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20 focus:outline-none transition resize-none ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
              }`}
              rows={3}
            />
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseReason('');
                }}
                className={`px-4 py-2 rounded-xl border font-medium transition ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (selectedFlow) {
                    confirmToggle(selectedFlow, pauseReason || null);
                  }
                }}
                className="px-4 py-2 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition"
              >
                Confirmar Pausa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
